"use client";

import {
  type AssignToValue,
  assignToDisplayLabel,
  deriveNameFromEmail,
  filterOrgUsers,
  findOrgUserByEmail,
  formatOrgUserLabel,
  isValidAssignEmail,
} from "@/components/inspection/assign-to-utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { InspectionOrgUser } from "@/lib/inspection-portal-api";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown, UserPlus } from "lucide-react";
import { useMemo, useState } from "react";

type AssignToComboboxProps = {
  users: InspectionOrgUser[];
  value: AssignToValue | null;
  onValueChange: (value: AssignToValue | null) => void;
  disabled?: boolean;
  id?: string;
  className?: string;
};

export function AssignToCombobox({
  users,
  value,
  onValueChange,
  disabled = false,
  id,
  className,
}: AssignToComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [inviteEmailPending, setInviteEmailPending] = useState<string | null>(
    null,
  );
  const [inviteName, setInviteName] = useState("");

  const filteredUsers = useMemo(
    () => filterOrgUsers(users, search),
    [users, search],
  );

  const trimmedSearch = search.trim();
  const exactMatch = findOrgUserByEmail(users, trimmedSearch);
  const showInviteOption =
    isValidAssignEmail(trimmedSearch) && exactMatch === undefined;

  const displayText = value
    ? assignToDisplayLabel(value)
    : "Search or enter email…";

  const selectUser = (user: InspectionOrgUser) => {
    onValueChange({
      type: "user",
      id: user.id,
      email: user.email,
      name: user.name,
    });
    setOpen(false);
    setSearch("");
  };

  const beginInvite = (email: string) => {
    setInviteEmailPending(email.trim());
    setInviteName(deriveNameFromEmail(email));
    setOpen(false);
    setSearch("");
  };

  const confirmInvite = () => {
    const email = inviteEmailPending?.trim() ?? "";
    const name = inviteName.trim();
    if (!email || !name) return;
    onValueChange({ type: "invite", email, name });
    setInviteEmailPending(null);
    setInviteName("");
  };

  const cancelInvite = () => {
    setInviteEmailPending(null);
    setInviteName("");
  };

  const portalContainer =
    typeof document !== "undefined"
      ? ((document.querySelector(".inspection-portal") as HTMLElement | null) ??
        undefined)
      : undefined;

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearch("");
        }}
        modal={true}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            // biome-ignore lint/a11y/useSemanticElements: custom combobox uses Button as trigger per design
            role="combobox"
            aria-expanded={open}
            aria-label={displayText}
            className={cn(
              "w-full justify-between font-normal",
              !value && "text-muted-foreground",
              className,
            )}
          >
            <span className="truncate">{displayText}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          container={portalContainer}
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command shouldFilter={false} className="flex flex-col">
            <CommandInput
              placeholder="Search users or type an email…"
              value={search}
              onValueChange={setSearch}
            />
            <ScrollArea className="max-h-[min(20rem,60vh)]">
              <CommandList>
                {filteredUsers.length === 0 && !showInviteOption ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {trimmedSearch
                      ? "No matching users. Enter a valid email to invite."
                      : "No users found."}
                  </div>
                ) : (
                  <CommandGroup>
                    {filteredUsers.map((user) => (
                      <CommandItem
                        key={user.id}
                        value={user.id}
                        keywords={[user.email, user.name ?? ""]}
                        onSelect={() => selectUser(user)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value?.type === "user" && value.id === user.id
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        {formatOrgUserLabel(user)}
                      </CommandItem>
                    ))}
                    {showInviteOption ? (
                      <CommandItem
                        value={`invite:${trimmedSearch}`}
                        onSelect={() => beginInvite(trimmedSearch)}
                      >
                        <UserPlus className="mr-2 h-4 w-4 shrink-0" />
                        Invite and assign {trimmedSearch}
                      </CommandItem>
                    ) : null}
                  </CommandGroup>
                )}
              </CommandList>
            </ScrollArea>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog
        open={inviteEmailPending != null}
        onOpenChange={(next) => {
          if (!next) cancelInvite();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite inspector</DialogTitle>
            <DialogDescription>
              Enter a name for{" "}
              <span className="font-medium text-foreground">
                {inviteEmailPending}
              </span>
              . They will be invited as an inspection-only user and assigned the
              selected properties.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="assign-invite-name">Name</Label>
            <Input
              id="assign-invite-name"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Full name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  confirmInvite();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={cancelInvite}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!inviteName.trim()}
              onClick={confirmInvite}
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
